import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';

const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const items = [
  { image: "/mainImages/LSR-Aware-PC.png", gameName: "Life-Saving Rules Aware" },
  { image: "/mainImages/LSR-Pro-PC.png", gameName: "Life-Saving Rules Pro" },
  { image: "/mainImages/LSR-Roads-PC.png", gameName: "Life-Saving Rules Road" },
  { image: "/mainImages/Driving-Aware-All-Roads-PC.png", gameName: "Driving Aware All Roads" },
  { image: "/mainImages/DrivingPro-Black-Top-PC.png", gameName: "Driving Pro Blacktop" },
  { image: "/mainImages/DrivingPro-Graded-Road-PC.png", gameName: "Driving Pro Graded Road" },
  { image: "/bundleimages/processsafetylockout.png", gameName: "Process Safety" }
];

const GamePlay = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [selectedGame, setSelectedGame] = useState(null);
  const [showPinModal, setShowPinModal] = useState(false);
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handlePlayClick = (e, game) => {
    e.preventDefault();
    setSelectedGame(game);
    setShowPinModal(true);
    setPin('');
    setError('');
  };

  const handlePinSubmit = async (e) => {
    e.preventDefault();
    if (!pin) {
      setError('Please enter your Play PIN.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${BACKEND_URL}/licenses/game-url`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          pin: pin.trim(),
          game_name: selectedGame.gameName,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to verify PIN');
      }

      // PIN is valid, open the CloudFront Signed URL in a new window
      window.open(data.url, '_blank');
      setShowPinModal(false);
      setPin('');
    } catch (err) {
      setError(err.message || 'Verification failed. Please check your PIN.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="gameplay-section">
      <p className="module-title"> Play Game</p>
      <p className="play-subtitle">Select a safety training module and enter your Play PIN to begin your session</p>
      
      {user && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginBottom: '25px' }}>
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              backgroundColor: '#214DA0',
              color: '#fff',
              padding: '10px 20px',
              borderRadius: '8px',
              border: 'none',
              fontWeight: 'bold',
              cursor: 'pointer',
              fontSize: '15px',
              boxShadow: '0 4px 12px rgba(33, 77, 160, 0.2)'
            }}
          >
            View My Licenses
          </button>
          {user.role === 'ADMIN' && (
            <button
              onClick={() => navigate('/admin')}
              style={{
                backgroundColor: '#2e7d32',
                color: '#fff',
                padding: '10px 20px',
                borderRadius: '8px',
                border: 'none',
                fontWeight: 'bold',
                cursor: 'pointer',
                fontSize: '15px'
              }}
            >
              Admin Panel
            </button>
          )}
        </div>
      )}
      
      <div className="play-gap">
        <div className="checkout-container-business">
          {items.map((item, index) => {
            const fileName = item.image.split("/").pop().split(".")[0];
            return (
              <div key={index} className="item game-card" onClick={(e) => handlePlayClick(e, item)}>
                <div className="card-image-wrapper">
                  <img src={item.image} alt={fileName} />
                  <div className="play-hover-overlay">
                    <span className="play-icon">
                      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="6" y1="12" x2="10" y2="12"></line>
                        <line x1="8" y1="10" x2="8" y2="14"></line>
                        <circle cx="15" cy="13" r="0.6" fill="currentColor" stroke="none"></circle>
                        <circle cx="17.5" cy="10.5" r="0.6" fill="currentColor" stroke="none"></circle>
                        <path d="M6.5 7h11a4 4 0 0 1 4 4.6l-.9 5.4a2.6 2.6 0 0 1-4.6 1.2l-1.6-2.1a2 2 0 0 0-1.6-.8h-1.6a2 2 0 0 0-1.6.8l-1.6 2.1a2.6 2.6 0 0 1-4.6-1.2l-.9-5.4A4 4 0 0 1 6.5 7z"></path>
                      </svg>
                    </span>
                  </div>
                </div>
                <div className="description-box-play" id="color-change">
                  Play Game
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Modern PIN Verification Modal */}
      {showPinModal && (
        <div className="modal-backdrop">
          <div className="modal-content auth-card">
            <button className="modal-close-btn" onClick={() => setShowPinModal(false)}>&times;</button>
            
            <h3 className="modal-title">Enter Play PIN</h3>
            <p className="modal-subtitle">
              Verify your license for <strong>{selectedGame?.gameName}</strong> to start playing.
            </p>

            {error && <div className="auth-alert error">{error}</div>}

            <form onSubmit={handlePinSubmit} className="auth-form">
              <div className="form-group">
                <input
                  type="text"
                  placeholder="e.g. WMN3-FR8L-G8EN"
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  maxLength={30}
                  className="pin-input-field"
                  disabled={loading}
                  autoFocus
                />
              </div>

              <div className="pin-hint-text">
                * Note: Every PIN verification decrements your remaining plays (max 10 runs per license).
              </div>

              <button type="submit" className="auth-submit-button" disabled={loading}>
                {loading ? 'Verifying PIN...' : 'Verify & Play'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GamePlay;